<template>
  <div class="shop-page">
    <!-- HEADER: Points balance -->
    <div class="shop-header">
      <div>
        <h1 class="page-title">{{ $t('player.shop.title') }}</h1>
        <p class="page-subtitle">{{ $t('player.shop.subtitle') }}</p>
      </div>
      <div class="points-balance-card">
        <div class="points-balance-label">{{ $t('player.shop.your_points') }}</div>
        <div v-if="loadingBalance" class="skel-line" style="width: 80px; height: 32px; border-radius: 6px;"></div>
        <div v-else class="points-balance-value">{{ pointsBalance.toLocaleString() }}</div>
        <div class="points-balance-sub">{{ $t('player.shop.spendable_points') }}</div>
      </div>
    </div>

    <!-- PAGE TABS -->
    <div class="page-tabs">
      <button
        class="page-tab"
        :class="{ active: activeTab === 'rewards' }"
        @click="switchTab('rewards')"
      >
        {{ $t('player.shop.tab_rewards') }}
      </button>
      <button
        class="page-tab"
        :class="{ active: activeTab === 'history' }"
        @click="switchTab('history')"
      >
        {{ $t('player.shop.tab_history') }}
      </button>
      <button
        class="page-tab"
        :class="{ active: activeTab === 'points' }"
        @click="switchTab('points')"
      >
        {{ $t('player.shop.tab_points') }}
      </button>
    </div>

    <!-- REWARDS TAB CONTENT -->
    <div v-if="activeTab === 'rewards'">
    <!-- FILTERS ROW -->
    <div class="filters-row">
      <!-- Category tabs -->
      <div class="category-tabs">
        <button
          class="category-tab"
          :class="{ active: selectedCategory === null }"
          @click="selectCategory(null)"
        >
          {{ $t('player.shop.all_rewards') }}
          <span v-if="allItems.length" class="tab-count">{{ allItems.length }}</span>
        </button>
        <button
          v-for="cat in categories"
          :key="cat.id"
          class="category-tab"
          :class="{ active: selectedCategory === cat.slug }"
          @click="selectCategory(cat.slug)"
        >
          {{ cat.name }}
          <span v-if="cat.items_count" class="tab-count">{{ cat.items_count }}</span>
        </button>
      </div>

      <!-- Affordable toggle -->
      <label class="affordable-toggle">
        <input
          v-model="affordableOnly"
          type="checkbox"
          class="toggle-checkbox"
          @change="() => loadRewards()"
        />
        <span class="toggle-track"></span>
        <span class="toggle-label">{{ $t('player.shop.affordable_only') }}</span>
      </label>
    </div>

    <!-- LOADING STATE -->
    <div v-if="loadingRewards" class="rewards-grid">
      <div v-for="i in 6" :key="i" class="reward-card-skeleton">
        <div class="skel-img"></div>
        <div class="skel-body">
          <div class="skel-line wide"></div>
          <div class="skel-line"></div>
          <div class="skel-line" style="width: 40%;"></div>
        </div>
      </div>
    </div>

    <!-- ERROR STATE -->
    <div v-else-if="error" class="error-state">
      <p>{{ error }}</p>
      <button class="btn-retry" @click="() => loadRewards()">{{ $t('common.try_again') }}</button>
    </div>

    <!-- EMPTY STATE -->
    <div v-else-if="rewards.length === 0" class="empty-state">
      <div class="empty-icon">🎁</div>
      <p class="empty-title">{{ $t('player.shop.no_rewards') }}</p>
      <p class="empty-sub">
        {{ affordableOnly ? $t('player.shop.no_rewards_affordable') : $t('player.shop.no_rewards_category') }}
      </p>
    </div>

    <!-- REWARDS GRID -->
    <div v-else class="rewards-grid">
      <div
        v-for="item in rewards"
        :key="item.id"
        class="reward-card"
        :class="{ unaffordable: !item.affordable || !item.meets_vip_requirement }"
        @click="openDetail(item)"
      >
        <!-- Image / Placeholder -->
        <div class="reward-card-image">
          <img
            v-if="item.image_path"
            :src="item.image_path"
            :alt="item.name"
            class="reward-img"
          />
          <div v-else class="reward-img-placeholder">
            <span class="reward-type-emoji">{{ typeEmoji(item.reward_type) }}</span>
          </div>

          <!-- VIP badge -->
          <div v-if="item.vip_tier_requirement !== null" class="vip-badge">
            <span class="vip-badge-text">{{ vipTierName(item.vip_tier_requirement) }}+</span>
          </div>

          <!-- Out of stock -->
          <div v-if="item.stock_quantity !== null && item.stock_quantity === 0" class="out-of-stock-overlay">
            {{ $t('player.shop.out_of_stock') }}
          </div>
        </div>

        <!-- Card body -->
        <div class="reward-card-body">
          <div class="reward-type-badge" :class="typeBadgeClass(item.reward_type)">
            {{ typeLabel(item.reward_type) }}
          </div>
          <h3 class="reward-name">{{ item.name }}</h3>

          <div class="reward-meta">
            <!-- Stock indicator -->
            <span v-if="item.stock_quantity !== null" class="stock-indicator" :class="item.stock_quantity > 0 ? 'in-stock' : 'no-stock'">
              {{ item.stock_quantity > 0 ? $t('player.shop.stock_left', { n: item.stock_quantity }) : $t('player.shop.out_of_stock') }}
            </span>
            <span v-else class="stock-indicator in-stock">{{ $t('player.shop.in_stock') }}</span>
          </div>
        </div>

        <!-- Card footer: cost -->
        <div class="reward-card-footer">
          <div class="reward-cost" :class="{ 'cost-unaffordable': !item.affordable }">
            <span class="cost-icon">⭐</span>
            <span class="cost-value">{{ item.point_cost.toLocaleString() }}</span>
            <span class="cost-label">pts</span>
          </div>
          <button
            class="btn-view"
            :class="{ 'btn-view-unaffordable': !item.affordable || !item.meets_vip_requirement }"
          >
            {{ $t('player.shop.view') }}
          </button>
        </div>
      </div>
    </div>

    </div><!-- end rewards tab -->

    <!-- HISTORY TAB CONTENT -->
    <div v-if="activeTab === 'history'">
      <!-- Loading state -->
      <div v-if="loadingRedemptions" class="redemptions-list">
        <div v-for="i in 5" :key="i" class="redemption-row-skeleton">
          <div class="skel-line wide"></div>
          <div class="skel-line" style="width: 35%;"></div>
        </div>
      </div>

      <!-- Error state -->
      <div v-else-if="redemptionsError" class="error-state">
        <p>{{ redemptionsError }}</p>
        <button class="btn-retry" @click="() => loadRedemptions()">{{ $t('common.try_again') }}</button>
      </div>

      <!-- Empty state -->
      <div v-else-if="redemptions.length === 0" class="empty-state">
        <div class="empty-icon">🛒</div>
        <p class="empty-title">{{ $t('player.shop.no_redemptions') }}</p>
        <p class="empty-sub">{{ $t('player.shop.no_redemptions_hint') }}</p>
        <button class="btn-retry" @click="switchTab('rewards')">{{ $t('player.shop.tab_rewards') }}</button>
      </div>

      <!-- Redemptions table -->
      <div v-else>
        <div class="redemptions-table-wrap">
          <table class="redemptions-table">
            <thead>
              <tr>
                <th>{{ $t('player.shop.col_reward') }}</th>
                <th>{{ $t('player.shop.col_type') }}</th>
                <th>{{ $t('player.shop.col_points') }}</th>
                <th>{{ $t('player.shop.col_status') }}</th>
                <th>{{ $t('player.shop.col_redeemed') }}</th>
                <th>{{ $t('player.shop.col_fulfilled') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in redemptions" :key="r.id">
                <td class="col-name">{{ r.reward_name ?? 'Unknown Reward' }}</td>
                <td class="col-type">
                  <span v-if="r.reward_type" class="reward-type-badge" :class="typeBadgeClass(r.reward_type)">
                    {{ typeLabel(r.reward_type) }}
                  </span>
                  <span v-else class="text-muted">—</span>
                </td>
                <td class="col-points">
                  <span class="points-spent-value">⭐ {{ r.points_spent.toLocaleString() }}</span>
                </td>
                <td class="col-status">
                  <span class="status-badge" :class="statusBadgeClass(r.status)">
                    {{ statusLabel(r.status) }}
                  </span>
                </td>
                <td class="col-date">{{ formatDate(r.created_at) }}</td>
                <td class="col-date">{{ r.fulfilled_at ? formatDate(r.fulfilled_at) : '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div v-if="redemptionsMeta.last_page > 1" class="pagination">
          <button
            class="page-btn"
            :disabled="redemptionsMeta.current_page <= 1"
            @click="() => loadRedemptions(redemptionsMeta.current_page - 1)"
          >
            {{ $t('common.pagination_prev') }}
          </button>
          <span class="page-info">
            {{ $t('common.page_of', { current: redemptionsMeta.current_page, total: redemptionsMeta.last_page }) }}
            <span class="page-total">{{ $t('common.total_count', { n: redemptionsMeta.total.toLocaleString() }) }}</span>
          </span>
          <button
            class="page-btn"
            :disabled="redemptionsMeta.current_page >= redemptionsMeta.last_page"
            @click="() => loadRedemptions(redemptionsMeta.current_page + 1)"
          >
            {{ $t('common.pagination_next') }}
          </button>
        </div>
      </div>
    </div><!-- end history tab -->

    <!-- POINTS HISTORY TAB CONTENT -->
    <div v-if="activeTab === 'points'">
      <!-- Loading state -->
      <div v-if="loadingPoints" class="redemptions-list">
        <div v-for="i in 5" :key="i" class="redemption-row-skeleton">
          <div class="skel-line wide"></div>
          <div class="skel-line" style="width: 35%;"></div>
        </div>
      </div>

      <!-- Error state -->
      <div v-else-if="pointsError" class="error-state">
        <p>{{ pointsError }}</p>
        <button class="btn-retry" @click="() => loadPointsHistory()">{{ $t('common.try_again') }}</button>
      </div>

      <!-- Empty state -->
      <div v-else-if="pointsHistory.length === 0" class="empty-state">
        <div class="empty-icon">⭐</div>
        <p class="empty-title">{{ $t('player.shop.no_points') }}</p>
        <p class="empty-sub">{{ $t('player.shop.no_points_hint') }}</p>
        <button class="btn-retry" @click="switchTab('rewards')">{{ $t('player.shop.tab_rewards') }}</button>
      </div>

      <!-- Points history table -->
      <div v-else>
        <div class="redemptions-table-wrap">
          <table class="redemptions-table">
            <thead>
              <tr>
                <th>{{ $t('player.shop.col_date') }}</th>
                <th>{{ $t('player.shop.col_type') }}</th>
                <th>{{ $t('player.shop.col_points') }}</th>
                <th>{{ $t('player.shop.col_reason') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="tx in pointsHistory" :key="tx.id">
                <td class="col-date">{{ formatDate(tx.created_at) }}</td>
                <td class="col-type">
                  <span class="points-type-badge" :class="tx.type === 'earned' ? 'badge-earned' : 'badge-spent'">
                    {{ tx.type === 'earned' ? $t('player.shop.points_earned') : $t('player.shop.points_spent_label') }}
                  </span>
                </td>
                <td class="col-points">
                  <span class="points-delta" :class="tx.type === 'earned' ? 'delta-earned' : 'delta-spent'">
                    {{ tx.type === 'earned' ? '+' : '-' }}{{ tx.points.toLocaleString() }}
                  </span>
                </td>
                <td class="col-reason">{{ tx.reason }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div v-if="pointsMeta.last_page > 1" class="pagination">
          <button
            class="page-btn"
            :disabled="pointsMeta.current_page <= 1"
            @click="() => loadPointsHistory(pointsMeta.current_page - 1)"
          >
            {{ $t('common.pagination_prev') }}
          </button>
          <span class="page-info">
            {{ $t('common.page_of', { current: pointsMeta.current_page, total: pointsMeta.last_page }) }}
            <span class="page-total">{{ $t('common.total_count', { n: pointsMeta.total.toLocaleString() }) }}</span>
          </span>
          <button
            class="page-btn"
            :disabled="pointsMeta.current_page >= pointsMeta.last_page"
            @click="() => loadPointsHistory(pointsMeta.current_page + 1)"
          >
            {{ $t('common.pagination_next') }}
          </button>
        </div>
      </div>
    </div><!-- end points history tab -->

    <!-- DETAIL MODAL -->
    <div v-if="detailItem" class="modal-overlay" @click.self="closeDetail">
      <div class="modal-box">

        <!-- ── Detail View ── -->
        <template v-if="!confirmMode && !redeemSuccess">
          <!-- Image -->
          <div class="modal-image">
            <img
              v-if="detailItem.image_path"
              :src="detailItem.image_path"
              :alt="detailItem.name"
              class="modal-img"
            />
            <div v-else class="modal-img-placeholder">
              <span class="modal-type-emoji">{{ typeEmoji(detailItem.reward_type) }}</span>
            </div>
          </div>

          <!-- Content -->
          <div class="modal-content">
            <div class="modal-type-badge" :class="typeBadgeClass(detailItem.reward_type)">
              {{ typeLabel(detailItem.reward_type) }}
            </div>
            <h2 class="modal-title">{{ detailItem.name }}</h2>

            <p v-if="detailItem.description" class="modal-description">{{ detailItem.description }}</p>

            <!-- Details list -->
            <div class="modal-details">
              <div class="modal-detail-row">
                <span class="detail-label">{{ $t('player.shop.detail_point_cost') }}</span>
                <span class="detail-value detail-cost">⭐ {{ detailItem.point_cost.toLocaleString() }} pts</span>
              </div>
              <div class="modal-detail-row">
                <span class="detail-label">{{ $t('player.shop.detail_your_balance') }}</span>
                <span class="detail-value">{{ pointsBalance.toLocaleString() }} pts</span>
              </div>
              <div v-if="detailItem.stock_quantity !== null" class="modal-detail-row">
                <span class="detail-label">{{ $t('player.shop.detail_stock') }}</span>
                <span class="detail-value" :class="detailItem.stock_quantity > 0 ? 'text-success' : 'text-danger'">
                  {{ detailItem.stock_quantity > 0 ? $t('player.shop.detail_remaining', { n: detailItem.stock_quantity }) : $t('player.shop.out_of_stock') }}
                </span>
              </div>
              <div v-if="detailItem.vip_tier_requirement !== null" class="modal-detail-row">
                <span class="detail-label">{{ $t('player.shop.detail_vip_required') }}</span>
                <span class="detail-value" :class="detailItem.meets_vip_requirement ? 'text-success' : 'text-danger'">
                  {{ vipTierName(detailItem.vip_tier_requirement) }}
                  {{ detailItem.meets_vip_requirement ? '✓' : '✗' }}
                </span>
              </div>
            </div>

            <!-- Eligibility warnings -->
            <div v-if="!detailItem.affordable" class="eligibility-warning">
              {{ $t('player.shop.eligibility_points_needed', { n: (detailItem.point_cost - pointsBalance).toLocaleString() }) }}
            </div>
            <div v-else-if="!detailItem.meets_vip_requirement" class="eligibility-warning">
              {{ $t('player.shop.eligibility_vip_required', { tier: vipTierName(detailItem.vip_tier_requirement!) }) }}
            </div>

            <!-- Actions -->
            <div class="modal-actions">
              <button class="btn-modal-cancel" @click="closeDetail">{{ $t('common.close') }}</button>
              <button
                class="btn-redeem"
                :disabled="!detailItem.affordable || !detailItem.meets_vip_requirement || (detailItem.stock_quantity !== null && detailItem.stock_quantity === 0)"
                @click="startConfirm"
              >
                <span v-if="!detailItem.affordable">{{ $t('player.shop.not_enough_points') }}</span>
                <span v-else-if="!detailItem.meets_vip_requirement">{{ $t('player.shop.vip_required') }}</span>
                <span v-else-if="detailItem.stock_quantity !== null && detailItem.stock_quantity === 0">{{ $t('player.shop.out_of_stock') }}</span>
                <span v-else>{{ $t('player.shop.redeem_for', { n: detailItem.point_cost.toLocaleString() }) }}</span>
              </button>
            </div>
          </div>
        </template>

        <!-- ── Confirmation View ── -->
        <div v-else-if="confirmMode && !redeemSuccess" class="confirm-view">
          <div class="confirm-header">
            <div class="confirm-emoji">{{ typeEmoji(detailItem.reward_type) }}</div>
            <h2 class="confirm-title">{{ $t('player.shop.confirm_title') }}</h2>
            <p class="confirm-reward-name">{{ detailItem.name }}</p>
          </div>

          <div class="confirm-body">
            <div class="modal-details">
              <div class="modal-detail-row">
                <span class="detail-label">{{ $t('player.shop.detail_point_cost') }}</span>
                <span class="detail-value detail-cost">⭐ {{ detailItem.point_cost.toLocaleString() }} pts</span>
              </div>
              <div class="modal-detail-row">
                <span class="detail-label">{{ $t('player.shop.detail_your_balance') }}</span>
                <span class="detail-value">{{ pointsBalance.toLocaleString() }} pts</span>
              </div>
              <div class="modal-detail-row">
                <span class="detail-label">{{ $t('player.shop.detail_balance_after') }}</span>
                <span
                  class="detail-value"
                  :class="(pointsBalance - detailItem.point_cost) >= 0 ? 'text-success' : 'text-danger'"
                >
                  {{ (pointsBalance - detailItem.point_cost).toLocaleString() }} pts
                </span>
              </div>
            </div>

            <!-- Inline error -->
            <div v-if="redeemError" class="redeem-error">
              <span class="redeem-error-icon">⚠</span>
              {{ redeemError }}
            </div>

            <!-- Actions -->
            <div class="modal-actions">
              <button class="btn-modal-cancel" :disabled="redeeming" @click="confirmMode = false">
                {{ $t('common.back') }}
              </button>
              <button
                class="btn-redeem"
                :disabled="redeeming"
                @click="handleRedeem(detailItem)"
              >
                <span v-if="redeeming" class="btn-loading">
                  <span class="spinner"></span>
                  {{ $t('player.shop.redeeming') }}
                </span>
                <span v-else>{{ $t('player.shop.confirm_redeem') }}</span>
              </button>
            </div>
          </div>
        </div>

        <!-- ── Success View ── -->
        <div v-else class="success-view">
          <div class="success-icon-wrap">✓</div>
          <h2 class="success-title">{{ $t('player.shop.success_title') }}</h2>
          <p class="success-message">{{ redeemSuccess }}</p>
          <button class="btn-success-close" @click="closeDetail">{{ $t('common.close') }}</button>
        </div>

      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import apiClient from '@/api/client'

const { t } = useI18n()

interface RewardCategory {
  id: number
  name: string
  slug: string
  sort_order: number
  is_active: boolean
  items_count: number
}

interface RewardItem {
  id: number
  category_id: number
  name: string
  description: string | null
  image_path: string | null
  point_cost: number
  stock_quantity: number | null
  vip_tier_requirement: number | null
  reward_type: 'bonus_credits' | 'free_spins' | 'cashback_boost' | 'physical' | 'tournament_entry'
  reward_config: Record<string, unknown> | null
  is_active: boolean
  affordable: boolean
  meets_vip_requirement: boolean
  category?: RewardCategory
}

interface Redemption {
  id: number
  reward_item_id: number
  reward_name: string | null
  reward_type: string | null
  points_spent: number
  status: 'pending' | 'fulfilled' | 'shipped' | 'cancelled'
  admin_notes: string | null
  fulfilled_at: string | null
  created_at: string
}

interface RedemptionMeta {
  current_page: number
  last_page: number
  per_page: number
  total: number
}

interface PointsTransaction {
  id: number
  type: 'earned' | 'spent'
  points: number
  reason: string
  created_at: string
}

interface PointsMeta {
  current_page: number
  last_page: number
  per_page: number
  total: number
}

const pointsBalance = ref(0)
const loadingBalance = ref(false)
const categories = ref<RewardCategory[]>([])
const rewards = ref<RewardItem[]>([])
const allItems = ref<RewardItem[]>([])
const loadingRewards = ref(false)
const error = ref<string | null>(null)
const selectedCategory = ref<string | null>(null)
const affordableOnly = ref(false)
const detailItem = ref<RewardItem | null>(null)
const confirmMode = ref(false)
const redeemError = ref<string | null>(null)
const redeemSuccess = ref<string | null>(null)
const redeeming = ref(false)

const activeTab = ref<'rewards' | 'history' | 'points'>('rewards')
const redemptions = ref<Redemption[]>([])
const redemptionsMeta = ref<RedemptionMeta>({ current_page: 1, last_page: 1, per_page: 20, total: 0 })
const loadingRedemptions = ref(false)
const redemptionsError = ref<string | null>(null)

const pointsHistory = ref<PointsTransaction[]>([])
const pointsMeta = ref<PointsMeta>({ current_page: 1, last_page: 1, per_page: 20, total: 0 })
const loadingPoints = ref(false)
const pointsError = ref<string | null>(null)

const VIP_TIERS = computed<Record<number, string>>(() => ({
  0: t('common.vip.bronze'),
  1: t('common.vip.silver'),
  2: t('common.vip.gold'),
  3: t('common.vip.platinum'),
  4: t('common.vip.diamond'),
}))

onMounted(async () => {
  await Promise.all([loadBalance(), loadCategories(), loadRewards()])
})

async function loadBalance(): Promise<void> {
  loadingBalance.value = true
  try {
    const { data } = await apiClient.get('/api/shop/points-balance')
    pointsBalance.value = data.spendable_points as number
  } catch {
    // Balance defaults to 0 on error
  } finally {
    loadingBalance.value = false
  }
}

async function loadCategories(): Promise<void> {
  try {
    const { data } = await apiClient.get('/api/shop/categories')
    categories.value = data.data as RewardCategory[]
  } catch {
    // Categories default to empty on error
  }
}

async function loadRewards(): Promise<void> {
  loadingRewards.value = true
  error.value = null
  try {
    const params: Record<string, string | number> = {}
    if (selectedCategory.value) {
      params.category = selectedCategory.value
    }
    if (affordableOnly.value) {
      params.affordable_only = 1
    }
    const { data } = await apiClient.get('/api/shop/rewards', { params })
    rewards.value = data.data as RewardItem[]
    // Keep allItems (no filters) for tab counts
    if (!selectedCategory.value && !affordableOnly.value) {
      allItems.value = rewards.value
    }
  } catch {
    error.value = t('player.shop.load_error')
  } finally {
    loadingRewards.value = false
  }
}

async function selectCategory(slug: string | null): Promise<void> {
  selectedCategory.value = slug
  await loadRewards()
}

function openDetail(item: RewardItem): void {
  detailItem.value = item
  confirmMode.value = false
  redeemError.value = null
  redeemSuccess.value = null
}

function closeDetail(): void {
  detailItem.value = null
  confirmMode.value = false
  redeemError.value = null
  redeemSuccess.value = null
}

function startConfirm(): void {
  redeemError.value = null
  redeemSuccess.value = null
  confirmMode.value = true
}

async function handleRedeem(item: RewardItem): Promise<void> {
  redeeming.value = true
  redeemError.value = null
  try {
    const { data } = await apiClient.post('/api/shop/redeem', { reward_item_id: item.id })
    const status = (data.data?.status as string) ?? 'fulfilled'
    if (item.reward_type === 'physical' || status === 'pending') {
      redeemSuccess.value = t('player.shop.redeem_success_physical')
    } else if (item.reward_type === 'free_spins') {
      redeemSuccess.value = t('player.shop.redeem_success_free_spins')
    } else if (item.reward_type === 'cashback_boost') {
      redeemSuccess.value = t('player.shop.redeem_success_cashback')
    } else if (item.reward_type === 'tournament_entry') {
      redeemSuccess.value = t('player.shop.redeem_success_tournament')
    } else {
      redeemSuccess.value = t('player.shop.redeem_success_credits')
    }
    // Refresh balance and rewards immediately
    await Promise.all([loadBalance(), loadRewards()])
  } catch (err: unknown) {
    const axiosErr = err as { response?: { data?: { message?: string } } }
    redeemError.value = axiosErr.response?.data?.message ?? t('player.shop.redeem_error_fallback')
  } finally {
    redeeming.value = false
  }
}

function vipTierName(tier: number): string {
  return VIP_TIERS.value[tier] ?? `Tier ${tier}`
}

function typeLabel(type: string): string {
  const labels: Record<string, string> = {
    bonus_credits: t('player.shop.type_bonus_credits'),
    free_spins: t('player.shop.type_free_spins'),
    cashback_boost: t('player.shop.type_cashback_boost'),
    physical: t('player.shop.type_physical'),
    tournament_entry: t('player.shop.type_tournament_entry'),
  }
  return labels[type] ?? type
}

function typeEmoji(type: string): string {
  const emojis: Record<string, string> = {
    bonus_credits: '💰',
    free_spins: '🎰',
    cashback_boost: '♻️',
    physical: '📦',
    tournament_entry: '🏆',
  }
  return emojis[type] ?? '🎁'
}

function typeBadgeClass(type: string): string {
  const classes: Record<string, string> = {
    bonus_credits: 'badge-credits',
    free_spins: 'badge-spins',
    cashback_boost: 'badge-cashback',
    physical: 'badge-physical',
    tournament_entry: 'badge-tournament',
  }
  return classes[type] ?? 'badge-credits'
}

async function loadRedemptions(page = 1): Promise<void> {
  loadingRedemptions.value = true
  redemptionsError.value = null
  try {
    const { data } = await apiClient.get('/api/shop/redemptions', { params: { page } })
    redemptions.value = data.data as Redemption[]
    redemptionsMeta.value = data.meta as RedemptionMeta
  } catch {
    redemptionsError.value = t('player.shop.redemptions_error')
  } finally {
    loadingRedemptions.value = false
  }
}

async function loadPointsHistory(page = 1): Promise<void> {
  loadingPoints.value = true
  pointsError.value = null
  try {
    const { data } = await apiClient.get('/api/shop/points-history', { params: { page } })
    pointsHistory.value = data.data as PointsTransaction[]
    pointsMeta.value = data.meta as PointsMeta
  } catch {
    pointsError.value = t('player.shop.points_error')
  } finally {
    loadingPoints.value = false
  }
}

function switchTab(tab: 'rewards' | 'history' | 'points'): void {
  activeTab.value = tab
  if (tab === 'history' && redemptions.value.length === 0 && !loadingRedemptions.value) {
    void loadRedemptions()
  }
  if (tab === 'points' && pointsHistory.value.length === 0 && !loadingPoints.value) {
    void loadPointsHistory()
  }
}

function statusLabel(status: string): string {
  const labels: Record<string, string> = {
    pending: t('player.shop.status_pending'),
    fulfilled: t('player.shop.status_fulfilled'),
    shipped: t('player.shop.status_shipped'),
    cancelled: t('player.shop.status_cancelled'),
  }
  return labels[status] ?? status
}

function statusBadgeClass(status: string): string {
  const classes: Record<string, string> = {
    pending: 'status-pending',
    fulfilled: 'status-fulfilled',
    shipped: 'status-shipped',
    cancelled: 'status-cancelled',
  }
  return classes[status] ?? 'status-pending'
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}
</script>

<style scoped>
.shop-page {
  padding: 24px;
  max-width: 1100px;
  margin: 0 auto;
  animation: fadeUp 0.3s ease both;
}

@keyframes fadeUp {
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
}

/* ─── HEADER ─── */
.shop-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 28px;
  flex-wrap: wrap;
}

.page-title {
  font-family: var(--font-display, 'Russo One', sans-serif);
  font-size: 28px;
  color: var(--text-primary, #fff);
  margin-bottom: 6px;
}

.page-subtitle {
  font-size: 14px;
  color: var(--text-tertiary, #7f8fa4);
}

.points-balance-card {
  background: linear-gradient(135deg, rgba(255, 215, 0, 0.12) 0%, rgba(255, 167, 38, 0.08) 100%);
  border: 1px solid rgba(255, 215, 0, 0.2);
  border-radius: 12px;
  padding: 16px 24px;
  text-align: center;
  min-width: 160px;
}

.points-balance-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--accent-gold, #ffd700);
  margin-bottom: 6px;
}

.points-balance-value {
  font-family: var(--font-display, 'Russo One', sans-serif);
  font-size: 32px;
  color: var(--accent-gold, #ffd700);
  line-height: 1;
  margin-bottom: 4px;
}

.points-balance-sub {
  font-size: 11px;
  color: rgba(255, 215, 0, 0.6);
}

/* ─── FILTERS ─── */
.filters-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}

.category-tabs {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  padding-bottom: 0;
  flex: 1;
}

.category-tab {
  padding: 10px 16px;
  background: none;
  border: none;
  color: var(--text-secondary, #b1bad3);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 150ms ease;
  border-bottom: 2px solid transparent;
  position: relative;
  bottom: -1px;
  font-family: inherit;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 6px;
}

.category-tab.active {
  color: var(--accent-gold, #ffd700);
  border-bottom-color: var(--accent-gold, #ffd700);
}

.category-tab:hover:not(.active) {
  color: var(--text-primary, #fff);
}

.tab-count {
  background: rgba(255, 255, 255, 0.1);
  color: var(--text-tertiary, #7f8fa4);
  font-size: 10px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 9999px;
}

.category-tab.active .tab-count {
  background: rgba(255, 215, 0, 0.15);
  color: var(--accent-gold, #ffd700);
}

/* Toggle */
.affordable-toggle {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  white-space: nowrap;
}

.toggle-checkbox {
  display: none;
}

.toggle-track {
  width: 36px;
  height: 20px;
  background: var(--bg-tertiary, #213743);
  border-radius: 9999px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  transition: background 150ms ease;
  flex-shrink: 0;
}

.toggle-track::after {
  content: '';
  position: absolute;
  width: 14px;
  height: 14px;
  background: var(--text-tertiary, #7f8fa4);
  border-radius: 50%;
  top: 2px;
  left: 2px;
  transition: transform 150ms ease, background 150ms ease;
}

.toggle-checkbox:checked + .toggle-track {
  background: rgba(0, 231, 1, 0.2);
  border-color: var(--accent-primary, #00e701);
}

.toggle-checkbox:checked + .toggle-track::after {
  transform: translateX(16px);
  background: var(--accent-primary, #00e701);
}

.toggle-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-secondary, #b1bad3);
}

/* ─── REWARDS GRID ─── */
.rewards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 20px;
  animation: fadeUp 0.25s ease both;
}

/* ─── REWARD CARD ─── */
.reward-card {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  transition: all 250ms ease;
  display: flex;
  flex-direction: column;
}

.reward-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  border-color: rgba(255, 215, 0, 0.2);
}

.reward-card.unaffordable {
  opacity: 0.6;
}

.reward-card.unaffordable:hover {
  border-color: rgba(255, 255, 255, 0.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

/* Image */
.reward-card-image {
  position: relative;
  height: 140px;
  background: var(--bg-tertiary, #213743);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.reward-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.reward-img-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(255, 215, 0, 0.06) 0%, rgba(20, 117, 225, 0.06) 100%);
}

.reward-type-emoji {
  font-size: 48px;
  line-height: 1;
}

.vip-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  background: linear-gradient(135deg, rgba(255, 215, 0, 0.9) 0%, rgba(255, 167, 38, 0.9) 100%);
  border-radius: 9999px;
  padding: 3px 8px;
}

.vip-badge-text {
  font-size: 10px;
  font-weight: 700;
  color: #000;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.out-of-stock-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-secondary, #b1bad3);
}

/* Card body */
.reward-card-body {
  padding: 14px 16px 8px;
  flex: 1;
}

.reward-name {
  font-family: var(--font-display, 'Russo One', sans-serif);
  font-size: 15px;
  color: var(--text-primary, #fff);
  margin: 6px 0 8px;
  line-height: 1.3;
}

.reward-meta {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.stock-indicator {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 9999px;
}

.stock-indicator.in-stock {
  background: rgba(0, 231, 1, 0.1);
  color: var(--color-success, #00e701);
}

.stock-indicator.no-stock {
  background: rgba(237, 66, 69, 0.1);
  color: var(--color-danger, #ed4245);
}

/* Card footer */
.reward-card-footer {
  padding: 10px 16px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.reward-cost {
  display: flex;
  align-items: center;
  gap: 4px;
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
}

.cost-icon {
  font-size: 14px;
}

.cost-value {
  font-size: 18px;
  font-weight: 700;
  color: var(--accent-gold, #ffd700);
}

.cost-label {
  font-size: 11px;
  color: var(--text-tertiary, #7f8fa4);
}

.cost-unaffordable .cost-value {
  color: var(--text-tertiary, #7f8fa4);
}

.btn-view {
  padding: 6px 14px;
  background: rgba(255, 215, 0, 0.1);
  border: 1px solid rgba(255, 215, 0, 0.25);
  color: var(--accent-gold, #ffd700);
  font-size: 12px;
  font-weight: 700;
  border-radius: 6px;
  cursor: pointer;
  transition: all 150ms ease;
  font-family: inherit;
}

.btn-view:hover {
  background: rgba(255, 215, 0, 0.18);
  border-color: rgba(255, 215, 0, 0.5);
}

.btn-view-unaffordable {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: var(--text-tertiary, #7f8fa4);
}

/* ─── TYPE BADGES ─── */
.reward-type-badge,
.modal-type-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 9999px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.badge-credits   { background: rgba(0, 231, 1, 0.15);     color: var(--color-success, #00e701); }
.badge-spins     { background: rgba(255, 215, 0, 0.15);   color: var(--accent-gold, #ffd700); }
.badge-cashback  { background: rgba(237, 66, 69, 0.15);   color: var(--color-danger, #ed4245); }
.badge-physical  { background: rgba(20, 117, 225, 0.15);  color: var(--color-info, #1475e1); }
.badge-tournament{ background: rgba(155, 89, 182, 0.15);  color: #b97bdb; }

/* ─── SKELETON ─── */
.reward-card-skeleton {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  overflow: hidden;
}

.skel-img {
  height: 140px;
  background: linear-gradient(90deg, rgba(255,255,255,.04) 25%, rgba(255,255,255,.08) 50%, rgba(255,255,255,.04) 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

.skel-body {
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.skel-line {
  height: 13px;
  border-radius: 4px;
  width: 60%;
  background: linear-gradient(90deg, rgba(255,255,255,.04) 25%, rgba(255,255,255,.08) 50%, rgba(255,255,255,.04) 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

.skel-line.wide { width: 80%; }

@keyframes shimmer {
  0%   { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}

/* ─── EMPTY / ERROR ─── */
.empty-state,
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
  gap: 12px;
}

.empty-icon {
  font-size: 48px;
}

.empty-title {
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary, #fff);
}

.empty-sub {
  font-size: 14px;
  color: var(--text-tertiary, #7f8fa4);
  max-width: 360px;
  line-height: 1.6;
}

.btn-retry {
  margin-top: 4px;
  padding: 9px 20px;
  background: var(--accent-primary, #00e701);
  color: #000;
  font-size: 13px;
  font-weight: 700;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  font-family: inherit;
}

/* ─── DETAIL MODAL ─── */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  animation: fadeIn 150ms ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to   { opacity: 1; }
}

.modal-box {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  max-width: 500px;
  width: 100%;
  overflow: hidden;
  animation: slideUp 200ms ease;
}

@keyframes slideUp {
  from { transform: translateY(24px); opacity: 0; }
  to   { transform: translateY(0); opacity: 1; }
}

.modal-image {
  height: 180px;
  background: var(--bg-tertiary, #213743);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.modal-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.modal-img-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(255, 215, 0, 0.08) 0%, rgba(20, 117, 225, 0.08) 100%);
}

.modal-type-emoji {
  font-size: 64px;
  line-height: 1;
}

.modal-content {
  padding: 24px;
}

.modal-title {
  font-family: var(--font-display, 'Russo One', sans-serif);
  font-size: 22px;
  color: var(--text-primary, #fff);
  margin: 10px 0 12px;
}

.modal-description {
  font-size: 14px;
  color: var(--text-secondary, #b1bad3);
  line-height: 1.6;
  margin-bottom: 18px;
}

.modal-details {
  display: flex;
  flex-direction: column;
  gap: 0;
  margin-bottom: 16px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  overflow: hidden;
}

.modal-detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  font-size: 13px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
}

.modal-detail-row:last-child {
  border-bottom: none;
}

.detail-label {
  color: var(--text-tertiary, #7f8fa4);
}

.detail-value {
  font-weight: 600;
  color: var(--text-primary, #fff);
}

.detail-cost {
  color: var(--accent-gold, #ffd700);
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
}

.text-success { color: var(--color-success, #00e701); }
.text-danger  { color: var(--color-danger, #ed4245); }

.eligibility-warning {
  background: rgba(237, 66, 69, 0.08);
  border: 1px solid rgba(237, 66, 69, 0.2);
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
  color: var(--color-danger, #ed4245);
  margin-bottom: 16px;
  line-height: 1.5;
}

.modal-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.btn-modal-cancel {
  padding: 10px 20px;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: var(--text-secondary, #b1bad3);
  font-size: 13px;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  transition: all 150ms ease;
  font-family: inherit;
}

.btn-modal-cancel:hover:not(:disabled) {
  color: var(--text-primary, #fff);
  border-color: rgba(255, 255, 255, 0.25);
  background: var(--bg-tertiary, #213743);
}

.btn-modal-cancel:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-redeem {
  flex: 1;
  padding: 11px 20px;
  background: var(--accent-gold, #ffd700);
  color: #000;
  font-size: 13px;
  font-weight: 700;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all 150ms ease;
  font-family: inherit;
}

.btn-redeem:hover:not(:disabled) {
  background: #f5cc00;
  box-shadow: 0 0 24px rgba(255, 215, 0, 0.35);
  transform: translateY(-1px);
}

.btn-redeem:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
  background: var(--bg-tertiary, #213743);
  color: var(--text-secondary, #b1bad3);
}

/* ─── CONFIRMATION VIEW ─── */
.confirm-view {
  display: flex;
  flex-direction: column;
}

.confirm-header {
  padding: 32px 24px 20px;
  text-align: center;
  background: linear-gradient(180deg, var(--bg-tertiary, #213743) 0%, transparent 100%);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.confirm-emoji {
  font-size: 52px;
  line-height: 1;
  margin-bottom: 12px;
}

.confirm-title {
  font-family: var(--font-display, 'Russo One', sans-serif);
  font-size: 20px;
  color: var(--text-primary, #fff);
  margin: 0 0 6px;
}

.confirm-reward-name {
  font-size: 14px;
  color: var(--accent-gold, #ffd700);
  font-weight: 600;
  margin: 0;
}

.confirm-body {
  padding: 20px 24px 24px;
}

/* Inline redeem error */
.redeem-error {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background: rgba(237, 66, 69, 0.08);
  border: 1px solid rgba(237, 66, 69, 0.25);
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
  color: var(--color-danger, #ed4245);
  margin-bottom: 16px;
  line-height: 1.5;
}

.redeem-error-icon {
  flex-shrink: 0;
  font-size: 14px;
  margin-top: 1px;
}

/* Loading spinner in confirm button */
.btn-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(0, 0, 0, 0.3);
  border-top-color: #000;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ─── SUCCESS VIEW ─── */
.success-view {
  padding: 48px 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 12px;
}

.success-icon-wrap {
  width: 64px;
  height: 64px;
  background: rgba(0, 231, 1, 0.12);
  border: 2px solid var(--color-success, #00e701);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  color: var(--color-success, #00e701);
  font-weight: 700;
  margin-bottom: 4px;
}

.success-title {
  font-family: var(--font-display, 'Russo One', sans-serif);
  font-size: 22px;
  color: var(--text-primary, #fff);
  margin: 0;
}

.success-message {
  font-size: 15px;
  color: var(--text-secondary, #b1bad3);
  line-height: 1.6;
  max-width: 320px;
  margin: 0 0 8px;
}

.btn-success-close {
  padding: 11px 32px;
  background: var(--accent-gold, #ffd700);
  color: #000;
  font-size: 14px;
  font-weight: 700;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all 150ms ease;
  font-family: inherit;
  margin-top: 4px;
}

.btn-success-close:hover {
  background: #f5cc00;
  box-shadow: 0 0 20px rgba(255, 215, 0, 0.3);
  transform: translateY(-1px);
}

/* ─── RESPONSIVE ─── */
@media (max-width: 768px) {
  .shop-page {
    padding: 16px;
  }

  .shop-header {
    flex-direction: column;
    gap: 16px;
  }

  .points-balance-card {
    width: 100%;
  }

  .filters-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .rewards-grid {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 14px;
  }

  .reward-card-image {
    height: 110px;
  }

  .reward-name {
    font-size: 13px;
  }

  .modal-box {
    max-width: 100%;
    border-radius: 12px;
  }

  .modal-actions {
    flex-direction: column-reverse;
  }

  .btn-modal-cancel {
    width: 100%;
    text-align: center;
  }

  .confirm-body .modal-actions {
    flex-direction: column-reverse;
  }
}

@media (max-width: 480px) {
  .page-title {
    font-size: 22px;
  }

  .points-balance-value {
    font-size: 26px;
  }

  .category-tabs {
    overflow-x: auto;
    padding-bottom: 2px;
    flex-wrap: nowrap;
  }
}

/* ─── PAGE TABS ─── */
.page-tabs {
  display: flex;
  gap: 0;
  border-bottom: 2px solid rgba(255, 255, 255, 0.06);
  margin-bottom: 24px;
}

.page-tab {
  padding: 12px 24px;
  background: none;
  border: none;
  color: var(--text-secondary, #b1bad3);
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 150ms ease;
  border-bottom: 2px solid transparent;
  position: relative;
  bottom: -2px;
  font-family: inherit;
  white-space: nowrap;
}

.page-tab.active {
  color: var(--accent-gold, #ffd700);
  border-bottom-color: var(--accent-gold, #ffd700);
}

.page-tab:hover:not(.active) {
  color: var(--text-primary, #fff);
  background: rgba(255, 255, 255, 0.04);
}

/* ─── REDEMPTIONS TABLE ─── */
.redemptions-table-wrap {
  overflow-x: auto;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  animation: fadeUp 0.25s ease both;
}

.redemptions-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.redemptions-table thead tr {
  background: rgba(255, 255, 255, 0.03);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.redemptions-table th {
  padding: 12px 16px;
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-tertiary, #7f8fa4);
  white-space: nowrap;
}

.redemptions-table tbody tr {
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  transition: background 100ms ease;
}

.redemptions-table tbody tr:last-child {
  border-bottom: none;
}

.redemptions-table tbody tr:hover {
  background: rgba(255, 255, 255, 0.03);
}

.redemptions-table td {
  padding: 14px 16px;
  color: var(--text-primary, #fff);
  vertical-align: middle;
}

.col-name {
  font-weight: 600;
  max-width: 200px;
}

.col-type,
.col-points,
.col-status {
  white-space: nowrap;
}

.col-date {
  color: var(--text-secondary, #b1bad3);
  font-size: 12px;
  white-space: nowrap;
}

.points-spent-value {
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  font-weight: 700;
  color: var(--accent-gold, #ffd700);
}

.text-muted {
  color: var(--text-tertiary, #7f8fa4);
}

/* ─── STATUS BADGES ─── */
.status-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.status-pending   { background: rgba(255, 167, 38, 0.15);  color: #ffa726; }
.status-fulfilled { background: rgba(0, 231, 1, 0.12);     color: var(--color-success, #00e701); }
.status-shipped   { background: rgba(20, 117, 225, 0.15);  color: var(--color-info, #1475e1); }
.status-cancelled { background: rgba(237, 66, 69, 0.12);   color: var(--color-danger, #ed4245); }

/* ─── SKELETON (redemptions) ─── */
.redemptions-list {
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  overflow: hidden;
}

.redemption-row-skeleton {
  padding: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* ─── PAGINATION ─── */
.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-top: 24px;
  padding: 8px 0;
}

.page-btn {
  padding: 8px 18px;
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: var(--text-secondary, #b1bad3);
  font-size: 13px;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  transition: all 150ms ease;
  font-family: inherit;
}

.page-btn:hover:not(:disabled) {
  border-color: rgba(255, 215, 0, 0.3);
  color: var(--accent-gold, #ffd700);
  background: rgba(255, 215, 0, 0.06);
}

.page-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.page-info {
  font-size: 13px;
  color: var(--text-secondary, #b1bad3);
  white-space: nowrap;
}

.page-total {
  color: var(--text-tertiary, #7f8fa4);
  font-size: 12px;
}

@media (max-width: 768px) {
  .page-tab {
    padding: 10px 16px;
    font-size: 13px;
  }

  .redemptions-table th:nth-child(2),
  .redemptions-table td:nth-child(2) {
    display: none;
  }

  .redemptions-table th:nth-child(6),
  .redemptions-table td:nth-child(6) {
    display: none;
  }
}

/* ─── POINTS HISTORY ─── */
.points-type-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.badge-earned {
  background: rgba(0, 231, 1, 0.12);
  color: var(--color-success, #00e701);
}

.badge-spent {
  background: rgba(237, 66, 69, 0.12);
  color: var(--color-danger, #ed4245);
}

.points-delta {
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  font-weight: 700;
  font-size: 14px;
}

.delta-earned {
  color: var(--color-success, #00e701);
}

.delta-spent {
  color: var(--color-danger, #ed4245);
}

.col-reason {
  color: var(--text-secondary, #b1bad3);
  font-size: 12px;
  max-width: 280px;
}

@media (max-width: 768px) {
  /* Hide Reason column on mobile for points table */
  .redemptions-table th:nth-child(4):last-child,
  .redemptions-table td:nth-child(4):last-child {
    max-width: 120px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>
